import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { fleetKeys } from '@/hooks/fleet-keys';
import { ApiError, apiClient, fleetPath } from '@/lib/api-client';

interface CreateAssociationVars {
  downstreamName: string;
  tunnelProto: string;
}

interface DeleteAssociationVars {
  upstreamName: string;
  downstreamName: string;
}

/**
 * POST /associations — create a directed tunnel edge with `serverName` as the
 * upstream (e.g. tag an exit node downstream of a VPN server).
 */
export function useCreateAssociation(
  serverName: string,
): UseMutationResult<unknown, ApiError, CreateAssociationVars> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ downstreamName, tunnelProto }) =>
      apiClient.post<unknown>(fleetPath('/associations'), {
        upstream_name: serverName,
        downstream_name: downstreamName,
        tunnel_proto: tunnelProto,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetKeys.server(serverName) });
      void queryClient.invalidateQueries({ queryKey: fleetKeys.associations() });
    },
  });
}

/** DELETE /associations — remove a tunnel edge by its endpoint pair. */
export function useDeleteAssociation(
  serverName: string,
): UseMutationResult<unknown, ApiError, DeleteAssociationVars> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ upstreamName, downstreamName }) =>
      apiClient.delete<unknown>(fleetPath('/associations'), {
        upstream_name: upstreamName,
        downstream_name: downstreamName,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetKeys.server(serverName) });
      void queryClient.invalidateQueries({ queryKey: fleetKeys.associations() });
    },
  });
}
